/* eslint-disable complexity */
/* eslint-disable no-plusplus */
/* eslint-disable no-magic-numbers */
import isEqual from 'lodash/isEqual';
import { v4 as uuid } from 'uuid';
import DOMPurify from 'dompurify';

import {
  FinancialDatasetRequest,
  ForecastComparisonRequest,
  ForecastComparisonDataset,
  ForecastComparisonDetails,
  RowFormula,
  AvailableLineDataRefNode,
  ForecastListItem,
  ReportingTemplateFormula,
  LiveReportDetail,
  ReportTemplateDetail,
  ReportTemplateList,
  LiveReportList,
  LiveReportReferencedDatasource
} from '@futrli-sdk/predict';
import { WorkspaceMember } from '@futrli-sdk/people';
import {
  ConsolidatedForecastListItem,
  ConsolidatedLiveReportReferencedDatasource,
  Consolidation
} from '@futrli-sdk/consolidations';

import { LayoutItem as Layout } from './components/grid-layout';
import {
  CARD_TYPES,
  DataField,
  FinData,
  GROUPING_OPTIONS,
  LayoutData,
  PageSetup,
  PAGE_NUMBERS_DISPLAY,
  WIDGET_TYPES,
  WidgetData,
  ClassifierType,
  FinFieldOptions,
  ReportMappingData,
  MappingData,
  FIELD_TYPES,
  WidgetTypesOrder,
  AxisOptions,
  VALUE_TREATMENT,
  PieDonutData
} from './types';
import {
  actualForecastIds,
  UNAVAILABLE_DATASET_KIND,
  actualsFinDataForecastId,
  dummyForecastIds,
  dummyLiveForecastId,
  pageSizesOptions,
  staticFormulaRefs,
  textWidgetAllowedTags
} from './constants';
import { addOpacityToColor, getColorForChartData } from './utils-colors';
import { calculateData } from './utils-generic';
import { translateHeading, translateSubHeading } from '@futrli/backend-messages';
import { IntlShape } from 'react-intl';
import { NodeStaticId } from '../forecast/prediction-builder/types';

/**
 * deep comparison between two FinancialDatasetRequests. (used to check if we need to re-fetch financial data)
 */
export const areFinQueriesEqual = (queryArr1: FinancialDatasetRequest[], queryArr2: FinancialDatasetRequest[]) => {
  if (queryArr1.length !== queryArr2.length) {
    return false;
  }
  for (const [i, query1] of queryArr1.entries()) {
    const query2 = queryArr2[i];
    if (
      query1.forecast_id !== query2.forecast_id ||
      query1.start_date !== query2.start_date ||
      query1.period_count !== query2.period_count ||
      query1.period_offset !== query2.period_offset ||
      query1.period_type !== query2.period_type ||
      query1.fields.length !== query2.fields.length ||
      query1.include_assumptions !== query2.include_assumptions ||
      !isEqual(query1.selected_variances, query2.selected_variances)
    ) {
      return false;
    }
    // TODO: remove once we have a unified format that always includes .ref instead of strings
    const fields1 = ((query1.fields as unknown) as { ref: string }[]).map(f => f.ref || f);
    const fields2 = ((query2.fields as unknown) as { ref: string }[]).map(f => f.ref || f);
    for (const field of fields1) {
      if (!fields2.includes(field)) {
        return false;
      }
    }
  }
  return true;
};

/**
 * deep comparison between two ForecastComparisonRequest. (used to check if we need to re-fetch financial comparison data)
 */
export const areMultiForecastCompQueriesEqual = (
  query1: ForecastComparisonRequest | undefined,
  query2: ForecastComparisonRequest | undefined
) => {
  if ((query1 && !query2) || (query2 && !query1)) {
    return false;
  }
  if (!query1 && !query2) {
    return true;
  }
  if (
    query1?.period_count !== query2?.period_count ||
    query1?.period_offset !== query2?.period_offset ||
    query1?.period_type !== query2?.period_type ||
    query1?.fields.length !== query2?.fields.length ||
    query1?.comparisons.length !== query2?.comparisons.length ||
    query1?.selected_variances.length !== query2?.selected_variances.length ||
    Object.keys(query1?.datasets || {}).length !== Object.keys(query2?.datasets || {}).length
  ) {
    return false;
  }

  const q1FieldRefs = getComparisonFieldRefs(query1?.fields);
  const q2FieldRefs = getComparisonFieldRefs(query2?.fields);
  for (const q1FieldRef of q1FieldRefs) {
    if (!q2FieldRefs.includes(q1FieldRef)) {
      return false;
    }
  }
  for (const sv1 of query1?.selected_variances || []) {
    if (!query2?.selected_variances.find(sv2 => sv1.ref === sv2.ref)) {
      return false;
    }
  }
  const datasets1: (ForecastComparisonDataset | undefined)[] = [];
  const datasets2: (ForecastComparisonDataset | undefined)[] = [];
  let comp1: ForecastComparisonDetails[] = [...(query1?.comparisons || [])];
  let comp2: ForecastComparisonDetails[] = [...(query2?.comparisons || [])];
  Object.keys(query1?.datasets || {}).forEach(k => {
    if (query1) {
      const ds = (query1.datasets as Record<string, ForecastComparisonDataset | undefined>)[k];
      ds && datasets1.push(ds);
      if (ds) {
        comp1 = comp1.map(c => ({
          primary_dataset_id:
            c.primary_dataset_id === k ? `${ds.forecast_id}#${ds.start_date}#${ds.actuals_only}` : c.primary_dataset_id,
          comparative_dataset_id:
            c.comparative_dataset_id === k
              ? `${ds.forecast_id}#${ds.start_date}#${ds.actuals_only}`
              : c.comparative_dataset_id
        }));
      }
    }
  });
  Object.keys(query2?.datasets || {}).forEach(k => {
    if (query2) {
      const ds = (query2.datasets as Record<string, ForecastComparisonDataset | undefined>)[k];
      ds && datasets2.push(ds);
      if (ds) {
        comp2 = comp2.map(c => ({
          primary_dataset_id:
            c.primary_dataset_id === k ? `${ds.forecast_id}#${ds.start_date}#${ds.actuals_only}` : c.primary_dataset_id,
          comparative_dataset_id:
            c.comparative_dataset_id === k
              ? `${ds.forecast_id}#${ds.start_date}#${ds.actuals_only}`
              : c.comparative_dataset_id
        }));
      }
    }
  });
  for (const ds1 of datasets1) {
    if (
      !datasets1.find(
        ds2 =>
          ds1?.name === ds2?.name &&
          ds1?.forecast_id === ds2?.forecast_id &&
          ds1?.actuals_only === ds2?.actuals_only &&
          ds1?.start_date === ds2?.start_date
      )
    ) {
      return false;
    }
  }
  for (let i = 0; i < comp1.length; i++) {
    if (
      comp1[i].primary_dataset_id !== comp2[i].primary_dataset_id ||
      comp1[i].comparative_dataset_id !== comp2[i].comparative_dataset_id
    ) {
      return false;
    }
  }
  return true;
};

/**
 * deep comparison between 2 layout object arrays. (used to check if layout has changed and we need to patch report)
 */
export const arePageLayoutsEqual = (layout1: Layout[], layout2: Layout[]) => {
  if (layout1.length !== layout2.length) {
    return false;
  }
  for (let i = 0; i < layout1.length; i++) {
    if (
      layout1[i].i !== layout2[i].i ||
      layout1[i].w !== layout2[i].w ||
      layout1[i].h !== layout2[i].h ||
      layout1[i].x !== layout2[i].x ||
      layout1[i].y !== layout2[i].y
    ) {
      return false;
    }
  }
  return true;
};

/**
 * deep comparison between 2 layoutData object arrays. (used to check if layout data has changed and we need to patch report)
 */
export const areLayoutsDataEqual = (data1: LayoutData[], data2: LayoutData[]): boolean => {
  if (data1.length !== data2.length) {
    return false;
  }
  for (const ld1 of data1) {
    const ld2 = data2.find(e => e.i === ld1.i);
    if (!ld2 || !isEqual(JSON.parse(JSON.stringify(ld1)), JSON.parse(JSON.stringify(ld2)))) {
      return false;
    }
  }
  return true;
};

/**
 * Takes the fields from a ForecastComparisonRequest and returns an array of
 */
export const getComparisonFieldRefs = (fields?: ForecastComparisonRequest['fields']) => {
  if (!fields) {
    return [];
  }
  return fields.map(field => {
    if (typeof field === 'string') {
      return field;
    }
    return ((field as unknown) as { ref: string }).ref;
  });
};

/**
 * convert report object into data we need for state in report builder context
 */
export const getStateFromReport = (report: LiveReportDetail | ReportTemplateDetail) => {
  const pageSetup: PageSetup = {
    backgroundColor: (report.data as { pageSetup: PageSetup })?.pageSetup?.backgroundColor || '#fff',
    orientation: report.orientation || 'portrait',
    pageSize: (report.data as { pageSetup: PageSetup })?.pageSetup?.pageSize || pageSizesOptions[0].value,
    margins: (report.data as { pageSetup: PageSetup })?.pageSetup?.margins || [
      { label: 'Top', value: 0.5 },
      { label: 'Right', value: 0.5 },
      { label: 'Bottom', value: 0.5 },
      { label: 'Left', value: 0.5 }
    ],
    showMargins: (report.data as { pageSetup: PageSetup })?.pageSetup?.showMargins || false,
    zoom: (report.data as { pageSetup: PageSetup })?.pageSetup?.zoom || 1,
    pageNumbersDisplay:
      (report.data as { pageSetup: PageSetup })?.pageSetup?.pageNumbersDisplay || PAGE_NUMBERS_DISPLAY.PAGE_ONE_OF_ONE
  };
  // sanitizing text widgets to remove any malicious code that might have been inserted
  // by directly calling the API using a tool like postman
  const pageLayoutData = ((report.data as { pageLayoutData: LayoutData[] })?.pageLayoutData || [])
    .map(ld =>
      ld.type === CARD_TYPES.TEXT
        ? {
            ...ld,
            [CARD_TYPES.TEXT]: {
              html: DOMPurify.sanitize(ld[CARD_TYPES.TEXT]?.html || '', {
                ALLOWED_TAGS: textWidgetAllowedTags,
                ALLOWED_ATTR: ['style', 'src', 'class']
              })
            }
          }
        : ld
    )
    .filter((ld, index, array) => {
      // Keep only the first occurrence of each unique 'i' value
      return array.findIndex(item => item.i === ld.i) === index;
    });
  return {
    pageSetup,
    pageLayout: (report.data as { pageLayout: Layout[] })?.pageLayout || [],
    pageLayoutData,
    sourceTemplateId: (report.data as { sourceTemplateId: string | undefined })?.sourceTemplateId || '',
    userTimezone: (report.data as { userTimezone: string | undefined })?.userTimezone || '',
    reportMappingData: (report.data as { data_reference_mapping: ReportMappingData })?.data_reference_mapping
  };
};

/**
 * get default grouping which depends on the type of the DataField
 */
export const getDefaultGrouping = (
  field: DataField,
  type: WIDGET_TYPES | undefined,
  isConsolidation?: boolean
): GROUPING_OPTIONS[] => {
  const isCircular = type === WIDGET_TYPES.DONUT || type === WIDGET_TYPES.PIE;
  const isTable = type === WIDGET_TYPES.TABLE;
  if (field.classifier === 'driver-group') {
    return isCircular ? [GROUPING_OPTIONS.DRIVERS] : [GROUPING_OPTIONS.DRIVER_GROUPS, GROUPING_OPTIONS.DRIVERS];
  }
  if (field.classifier.includes('driver')) {
    return [GROUPING_OPTIONS.DRIVERS];
  }
  const gn = mapGroupNameToOption(field.classifier) as GROUPING_OPTIONS;
  if (!field.children?.length) {
    return gn ? [gn] : [];
  }
  if (
    isCircular &&
    [
      'static',
      'group',
      'elimination-group',
      'group-by-account-name',
      'group-by-org-group-name',
      'account',
      'eliminated-account',
      ''
    ].includes(field.classifier)
  ) {
    return [gn || GROUPING_OPTIONS.CATEGORIES];
  }
  let result: GROUPING_OPTIONS[] = [];
  if (isTable) {
    switch (field.classifier) {
      case '':
      case 'static':
        result = [
          GROUPING_OPTIONS.CATEGORIES,
          GROUPING_OPTIONS.GROUPS,
          GROUPING_OPTIONS.ACCOUNTS,
          GROUPING_OPTIONS.ASSUMPTIONS
        ];
        break;
      case 'group':
      case 'elimination-group':
      case 'group-by-account-name':
      case 'group-by-org-group-name':
        result = [GROUPING_OPTIONS.GROUPS, GROUPING_OPTIONS.ACCOUNTS, GROUPING_OPTIONS.ASSUMPTIONS];
        break;
      case 'account':
      case 'eliminated-account':
        result = [GROUPING_OPTIONS.ACCOUNTS, GROUPING_OPTIONS.ASSUMPTIONS];
        break;
      default:
        result = [gn];
    }
    return isConsolidation ? result.filter(e => e !== GROUPING_OPTIONS.ASSUMPTIONS) : result;
  } else {
    switch (field.classifier) {
      case '':
      case 'static':
        return [GROUPING_OPTIONS.CATEGORIES];
      case 'group':
      case 'elimination-group':
      case 'group-by-account-name':
      case 'group-by-org-group-name':
        return [GROUPING_OPTIONS.GROUPS];
      case 'account':
      case 'eliminated-account':
        return [GROUPING_OPTIONS.ACCOUNTS];
      default:
        return [gn];
    }
  }
};

/**
 *  returns GROUPING_OPTIONS value for the group name that comes from the API
 */
export const mapGroupNameToOption = (name: string) => {
  switch (name) {
    case 'static':
      return GROUPING_OPTIONS.CATEGORIES;
    case 'group':
    case 'elimination-group':
    case 'group-by-account-name':
    case 'group-by-org-group-name':
      return GROUPING_OPTIONS.GROUPS;
    case 'account':
    case 'eliminated-account':
      return GROUPING_OPTIONS.ACCOUNTS;
    case 'assumption':
      return GROUPING_OPTIONS.ASSUMPTIONS;
    case 'row-formula':
    case 'template-formula':
      return GROUPING_OPTIONS.FORMULAS;
    case 'driver-group':
      return GROUPING_OPTIONS.DRIVER_GROUPS;
    case 'freestyle-driver':
    case 'formula-driver':
      return GROUPING_OPTIONS.DRIVERS;
    default:
      return name;
  }
};

/**
 * map classifier (that comes from the API) to the grouping option used for table row formatting
 */
export const mapClassifierToTableRowOption = (name: string, ref: string): GROUPING_OPTIONS => {
  switch (name) {
    case 'static':
      return staticFormulaRefs.includes(ref) ? GROUPING_OPTIONS.FORMULAS : GROUPING_OPTIONS.CATEGORIES;
    case 'group':
    case 'elimination-group':
    case 'group-by-account-name':
    case 'group-by-org-group-name':
      return GROUPING_OPTIONS.GROUPS;
    case 'account':
    case 'eliminated-account':
      return GROUPING_OPTIONS.ACCOUNTS;
    case 'assumption':
      return GROUPING_OPTIONS.ASSUMPTIONS;
    case 'row-formula':
    case 'template-formula':
      return GROUPING_OPTIONS.FORMULAS;
    case 'driver-group':
      return GROUPING_OPTIONS.DRIVER_GROUPS;
    case 'freestyle-driver':
    case 'formula-driver':
      return GROUPING_OPTIONS.DRIVERS;
    default:
      return GROUPING_OPTIONS.CATEGORIES;
  }
};

/**
 * returns fields targeted by the selected grouping, with a level that is used for indentation
 */
export const getFieldsByGroupingWithLevel = (
  field: DataField,
  grouping: GROUPING_OPTIONS[],
  isSecondary: boolean,
  assumptions: { ref: string; name: string; parentRef: string }[] = [],
  excludedChildren: string[] = [],
  includedChildren: string[] = []
) => {
  const fields: {
    ref: string;
    name: string;
    level: number;
    classifier: ClassifierType;
    isSecondary: boolean;
    organisation_id?: string;
  }[] = [];

  // We want to exclude sub-organisation categories if the user selects a consolidation category
  const excludeSubOrgFields =
    grouping.length === 1 &&
    grouping[0] === GROUPING_OPTIONS.CATEGORIES &&
    field.classifier === 'static' &&
    !field.organisation_id;

  const recursivelyAddFields = (f: DataField, level: number, excludeNext: boolean) => {
    let newLevel = level;
    let shouldExcludeNext = excludeNext;
    if (excludedChildren.includes(f.ref)) {
      shouldExcludeNext = true;
    }
    if (includedChildren.includes(f.ref)) {
      shouldExcludeNext = false;
    }
    if (
      !f.disabled &&
      f.classifier !== 'assumption' &&
      (grouping.includes(f.classifier as GROUPING_OPTIONS) ||
        grouping.includes(mapGroupNameToOption(f.classifier) as GROUPING_OPTIONS)) &&
      (!excludeSubOrgFields || !f.organisation_id) &&
      !shouldExcludeNext
    ) {
      fields.push({
        ref: f.ref,
        name: f.name,
        level,
        classifier: f.classifier as ClassifierType,
        isSecondary,
        organisation_id: f.organisation_id
      });
      ++newLevel;
    }
    if (f.classifier === 'account' && grouping.includes(GROUPING_OPTIONS.ASSUMPTIONS)) {
      fields.push(
        ...assumptions
          .filter(ass => ass.parentRef === f.ref)
          .map(ass => ({
            ref: ass.ref,
            name: ass.name,
            level: newLevel,
            classifier: 'assumption' as ClassifierType,
            isSecondary,
            organisation_id: f.organisation_id
          }))
      );
    } else {
      f.children?.forEach(child => recursivelyAddFields(child, newLevel, shouldExcludeNext));
    }
  };
  recursivelyAddFields(field, 0, false);
  return fields;
};

export const getFlattenedFormulas = (formulas: DataField[]) => {
  const flattenedFormulas: DataField[] = [];
  const recursivelyAddFormulas = (f: DataField) => {
    if (!f.children?.length) {
      flattenedFormulas.push(f);
    } else {
      f.children?.forEach(child => recursivelyAddFormulas(child));
    }
  };
  formulas.forEach(f => recursivelyAddFormulas(f));
  return flattenedFormulas;
};

/**
 * Finds the draft template formulas used by a template, so they can be published alongside it.
 * @param template - The report template to find the unpublished formulas within
 * @param formulas - The list of all template formulas that exist
 */
export const getUnpublishedFormulas = (template: { data?: unknown }, formulas: ReportingTemplateFormula[]) => {
  const formulaRefs: string[] = [];

  (template.data as { pageLayoutData?: LayoutData[] } | undefined)?.pageLayoutData?.forEach(pld => {
    pld?.[CARD_TYPES.WIDGET]?.selectedFinFields?.forEach(field => {
      if (field.classifier === 'row-formula') {
        formulaRefs.push(field.ref);
      }
    });
  });

  return formulaRefs
    .map(ref => formulas.find(f => f.ref === ref && f.status === 'draft'))
    .filter(f => !!f) as ReportingTemplateFormula[];
};

/**
 * Adding organisation short code next to field name
 * @param fields
 * @param consolidation, which contains organisations information
 * @returns
 */
export const addOrgCodeToFieldName = (
  fields: DataField[],
  consolidation: Consolidation
): AvailableLineDataRefNode[] => {
  return fields.map(field => {
    const org = consolidation.organisations.find(o => o.organisation_id === field.organisation_id);
    return {
      ...field,
      children: field.children?.length ? addOrgCodeToFieldName(field.children, consolidation) : field.children,
      name: org ? `${field.name} - ${org.short_code}` : field.name
    };
  }) as AvailableLineDataRefNode[];
};

/**
 * Changing from table to any graph will display all data in graph so we will see normal data + variance data
 * until request is not finished. This method is filtering data for charts to get data without variance columns so
 * we don't have invalid data on graphs for few seconds until request is not finished.
 */
export const getWidgetData = (
  data: (FinData | undefined)[],
  selectedVariances: DataField[],
  widgetType?: WIDGET_TYPES
): (FinData | undefined)[] => {
  if (widgetType === WIDGET_TYPES.TABLE || !selectedVariances || !selectedVariances.length) {
    return data;
  }

  const updatedData = data.map(
    d =>
      ({
        ...d,
        periods: (d?.periods || []).filter((_p, i) => {
          const fieldKey = data && d ? Object.keys(d.fields)[0] : undefined;
          return fieldKey ? d?.field_meta[fieldKey].column_meta[i].cell_type !== 'variance' : true;
        }),
        fields: {
          ...Object.keys(d?.fields || {}).reduce((acc, key) => {
            return {
              ...acc,
              [key]: d?.fields[key].filter((_f, i) => d?.field_meta[key].column_meta[i].cell_type !== 'variance')
            };
          }, {})
        }
      } as FinData)
  );

  return updatedData;
};

// localize headings and subheadings in FinData and compData
export const localizeFinData = (data: (FinData | undefined)[], intl: IntlShape): (FinData | undefined)[] =>
  data.map(
    d =>
      ({
        ...d,
        periods: (d?.column_info || []).map(p => (p.heading ? translateHeading(p.heading, intl) : ''))
      } as FinData)
  );

export const localizeCompData = (data: (FinData | undefined)[], intl: IntlShape): (FinData | undefined)[] =>
  data.map(
    d =>
      ({
        ...d,
        columns: (d?.column_info || []).map(p => (p.heading ? translateHeading(p.heading, intl) : '')),
        sub_headings: (d?.column_info || []).map(p => (p.sub_heading ? translateSubHeading(p.sub_heading, intl) : ''))
      } as FinData)
  );

/**
 * Derives the periods array from a comparison response's field_meta.
 * Extracts unique period strings from column_meta entries where is_first_in_period is true.
 * This allows us to avoid needing the financial-data response just for periods.
 */
export const getPeriodsFromComparisonFieldMeta = (
  fieldMeta: Record<string, { column_meta: { period: string; is_first_in_period: boolean }[] }>
): string[] => {
  const firstFieldKey = Object.keys(fieldMeta || {})[0];
  if (!firstFieldKey || !fieldMeta[firstFieldKey]?.column_meta) {
    return [];
  }

  return fieldMeta[firstFieldKey].column_meta.filter(cm => cm.is_first_in_period).map(cm => cm.period);
};

/**
 * Check in FinData returned by the API if we need to show increase or decrease arrow
 */
export const checkForArrowsInColumn = (columnIndex: number, data?: FinData) => {
  if (!data) {
    return false;
  }

  const tableRowKeys = Object.keys(data.field_meta);
  for (const k of tableRowKeys) {
    const columnMeta = data.field_meta[k].column_meta;
    if (!columnMeta) {
      return false;
    }
    const showArrow =
      columnMeta[columnIndex]?.change_direction === 'increase' ||
      columnMeta[columnIndex]?.change_direction === 'decrease';
    if (showArrow) {
      return true;
    }
  }

  return false;
};

/**
 * remove a specific DataField by ref from a list of fields and their children
 */
export const getFinFieldsWithoutChild = (fields: DataField[], refToRemove: string): DataField[] => {
  return fields
    .map(item => {
      return { ...item };
    })
    .filter(item => {
      if (item.children && item.children.length > 0) {
        item.children = getFinFieldsWithoutChild(item.children, refToRemove);
      }
      return item.ref !== refToRemove;
    });
};

/**
 * convert a tree of DataField object arrays to an array of DataField objects
 */
export const flattenFinFields = (fields: DataField[]) => {
  const flattenedFields: DataField[] = [];

  const recursivelyAddFields = (f: DataField, depth: number) => {
    if (!f.disabled) {
      flattenedFields.push({ ...f, depth });
    }
    f.children?.forEach(child => recursivelyAddFields(child, depth + 1));
  };
  for (const f of fields) {
    f && recursivelyAddFields(f, 0);
  }
  return flattenedFields;
};

/**
 * Recursively localize field names according to your rules
 * @param fields
 * @param nodeStaticIds
 * @returns
 */
export const localizeFieldNames = (fields: DataField[], nodeStaticIds: Record<NodeStaticId, string>): DataField[] => {
  return fields.map(f => {
    let name = f.name;

    if (f.has_active_switch_rule && f.account_type && nodeStaticIds[f.account_type]) {
      name = `${name} [${nodeStaticIds[f.account_type]}]`;
    } else if (f.classifier === 'static') {
      name = f.static_id ? nodeStaticIds[f.static_id] || name : name;
    } else if (f.classifier === '') {
      name = nodeStaticIds[f.ref as NodeStaticId] || name;
    }

    return {
      ...f,
      name,
      children: f.children ? localizeFieldNames(f.children, nodeStaticIds) : f.children
    };
  });
};

/**
 * convert a tree of DataField object arrays to an array of ref strings
 */
export const flattenFinFieldsToRefs = (fields: DataField[]) => {
  const flattenedFields: string[] = [];

  const recursivelyAddFields = (f: DataField) => {
    if (!f.disabled) {
      flattenedFields.push(f.ref);
    }
    f.children?.forEach(child => recursivelyAddFields(child));
  };

  for (const f of fields) {
    recursivelyAddFields(f);
  }
  return flattenedFields;
};

/**
 * convert a tree of nodes (E.G: DataFields) to a map of nodes indexed by ref
 */
export const getRefMap = <T extends { ref: string; disabled?: boolean; children?: T[] }>(nodes: T[]) => {
  const map: Record<string, T> = {};

  const recursivelyAddNodes = (node: T) => {
    if (!node.disabled) {
      map[node.ref] = node;
    }
    node.children?.forEach(child => recursivelyAddNodes(child));
  };

  for (const node of nodes) {
    recursivelyAddNodes(node);
  }
  return map;
};

/**
 * Get a field with its children by its ref, from a list of DataField objects
 */
export const getFieldWithChildren = (fields: DataField[], ref: string, isSecondary = false): DataField | undefined => {
  let field = fields.find(e => e.ref === ref);
  if (field) {
    return { ...field, isSecondary };
  } else {
    for (const elmt of fields) {
      if (elmt.children?.length) {
        field = getFieldWithChildren(elmt.children, ref, isSecondary);
        if (field) {
          return { ...field, isSecondary };
        }
      }
    }
    return field;
  }
};

/**
 * Returns true or false depending on if the given field has a child that appears in the given selectedFields array
 * @param field - Field to check for selected children
 * @param selectedFields - The array of selected fields
 */
export const isChildFieldSelected = (field: DataField, selectedFields: DataField[]): boolean => {
  if (!field.children?.length || !selectedFields.length) {
    return false;
  }
  if (field.children.some(e => selectedFields.some(f => f.ref === e.ref))) {
    return true;
  }
  return field.children.some(e => isChildFieldSelected(e, selectedFields));
};

/**
 * Actuals-only datasets ('actual-data' / 'prior-year-actuals') are requested with the 'actuals' forecast id.
 * The dataset id is still used as the key so the returned data can be matched back to the dataset.
 */
export const withActualsForecastId = (
  query: FinancialDatasetRequest,
  isFutrliTemplate = false
): FinancialDatasetRequest => {
  if (!actualForecastIds.includes(query.forecast_id)) {
    return query;
  }
  // Futrli templates have no real actuals, so request the dummy live forecast to get dummy data
  const forecastId = isFutrliTemplate ? dummyLiveForecastId : actualsFinDataForecastId;
  return { ...query, forecast_id: forecastId, consolidated_forecast_id: forecastId } as FinancialDatasetRequest;
};

/**
 * construct multi forecast query from queries array
 */
export const multiForecastQueryFromQueriesArr = (
  cardId: string,
  queries: FinancialDatasetRequest[],
  isFutrliTemplate = false
) => {
  const queryObject: Record<string, FinancialDatasetRequest> = {};
  queries.forEach(q => {
    queryObject[`${cardId}#|#${q.forecast_id}`] = withActualsForecastId(q, isFutrliTemplate);
  });
  return queryObject;
};

/**
 * breaks returned FinData by card and then forecastID
 */
export const convertFinDataToIncludeForecastId = (finData: Record<string, FinData>) => {
  const newData: Record<string, Record<string, FinData> | undefined> = {};
  Object.keys(finData).forEach(key => {
    const keyBits = key.split('#|#');
    const cardFinData = newData[keyBits[0]] || {};
    cardFinData[keyBits[1]] = { ...finData[key], forecastId: keyBits[1] };
    newData[keyBits[0]] = cardFinData;
  });
  return newData;
};

/**
 * remove anything that has to do with a fin field from pageLayoutData
 */
export const removeDeletedFinFields = (
  pageLayoutData: LayoutData[],
  allFinFields: DataField[],
  formulas: (RowFormula | ReportingTemplateFormula)[],
  mappingData: ReportMappingData | undefined
) => {
  if (!allFinFields?.length && !formulas?.length) {
    return pageLayoutData;
  }
  let deleted = false;
  let flattenedFields = flattenFinFieldsToRefs(allFinFields);
  if (mappingData?.line_data_references) {
    flattenedFields = [...Object.keys(mappingData.line_data_references), ...flattenedFields];
  }
  const newPLD = pageLayoutData.map(pld => {
    if (pld[CARD_TYPES.WIDGET]?.selectedFinFields.length) {
      const newSff = pld[CARD_TYPES.WIDGET]?.selectedFinFields.filter(
        f => flattenedFields.find(e => e === f.ref) || formulas.find(e => e.ref === f.ref)
      );
      if ((newSff?.length || 0) < (pld[CARD_TYPES.WIDGET]?.selectedFinFields.length || 0)) {
        deleted = true;
        return { ...pld, [CARD_TYPES.WIDGET]: { ...pld[CARD_TYPES.WIDGET], selectedFinFields: newSff } } as LayoutData;
      } else {
        return pld;
      }
    } else {
      return pld;
    }
  });
  return deleted ? newPLD : pageLayoutData;
};

/**
 * Set the isDeleted flag on all financial fields
 */
export const updateFinFields = (
  pageLayoutData: LayoutData[],
  allFinFields: DataField[],
  formulas: (RowFormula | ReportingTemplateFormula)[],
  mappingData: ReportMappingData | undefined
) => {
  if (!allFinFields?.length && !formulas?.length) {
    return pageLayoutData;
  }

  let hasChanges = false;

  const fieldRefMap = {
    ...getRefMap(allFinFields),
    ...getRefMap(formulas),
    ...mappingData?.line_data_references
  };

  const newPLD = pageLayoutData.map(pld => {
    const widgetData = pld[CARD_TYPES.WIDGET];
    const selectedFinFields = widgetData?.selectedFinFields;

    if (selectedFinFields) {
      const changesNeeded = selectedFinFields.find(field => {
        const isDeleted = !fieldRefMap[field.ref];
        const nameChanged = fieldRefMap[field.ref]?.name !== field.name;
        return !!field.isDeleted !== isDeleted || nameChanged;
      });

      if (changesNeeded) {
        hasChanges = true;
        return {
          ...pld,
          [CARD_TYPES.WIDGET]: {
            ...widgetData,
            selectedFinFields: selectedFinFields.map(field => ({
              ...field,
              name: fieldRefMap[field.ref]?.name || field.name,
              isDeleted: !fieldRefMap[field.ref]
            }))
          }
        };
      }
    }

    return pld;
  });

  return hasChanges ? newPLD : pageLayoutData;
};

/**
 * Detect fin fields that are not found in current fields/formulas and return them
 * as mapping entries to be added to dataReferenceMapping.line_data_references.
 * This allows the mapping modal to handle re-mapping of deleted/missing refs.
 */
export const getDeletedFinFieldMappingEntries = (
  pageLayoutData: LayoutData[],
  allFinFields: DataField[],
  formulas: (RowFormula | ReportingTemplateFormula)[],
  mappingData: ReportMappingData | undefined
): Record<string, MappingData> => {
  if (!allFinFields?.length && !formulas?.length) {
    return {};
  }

  const fieldRefMap = {
    ...getRefMap(allFinFields),
    ...getRefMap(formulas)
  };

  const entries: Record<string, MappingData> = {};

  pageLayoutData.forEach(pld => {
    const selectedFinFields = pld[CARD_TYPES.WIDGET]?.selectedFinFields;
    if (selectedFinFields) {
      selectedFinFields.forEach(field => {
        if (!fieldRefMap[field.ref] && !mappingData?.line_data_references?.[field.ref]) {
          entries[field.ref] = {
            id: field.ref,
            name: field.name || field.ref,
            type: 'account',
            data: {}
          };
        }
      });
    }
  });

  return entries;
};

/**
 * replaces refs (datasets or data refs) (origRefs) with mapped refs (mappedRefs) refs anywhere in the widget JSON
 * will affect selectedDatasets, selectedFinFields, selectedComparisons and finFieldsOptions
 * send allFields if you want to replace finFields, otherwise send an empty array.
 */
export const replaceRefsInWidget = (
  origWidgetData: WidgetData | undefined,
  origRefs: string[],
  mappedRefs: string[],
  allFields: DataField[]
) => {
  if (!origWidgetData) {
    return origWidgetData;
  }
  if (origRefs.length !== mappedRefs.length) {
    // eslint-disable-next-line no-console
    console.error('supplied origRefs and mappedRefs do not have the same length');
    return;
  }
  const replaceableDS: string[][] = [];
  origRefs.forEach((ds, i) => {
    if (ds !== mappedRefs[i]) {
      replaceableDS.push([ds, mappedRefs[i]]);
    }
  });
  if (replaceableDS.length) {
    let stringData = JSON.stringify(origWidgetData);
    replaceableDS.forEach(dsMap => {
      const re = new RegExp(`"${dsMap[0]}"`, 'g');
      stringData = stringData.replace(re, `"${dsMap[1]}"`);
    });
    const newWidgetData: WidgetData = JSON.parse(stringData);
    // replace the old selectedFinFields with new ones (to fix the name), and remove duplicates
    if (allFields.length && newWidgetData.selectedFinFields?.length) {
      newWidgetData.selectedFinFields = newWidgetData.selectedFinFields
        .filter((sf, index) => newWidgetData.selectedFinFields!.findIndex(e => e.ref === sf.ref) === index)
        .map(sf => {
          const newField = allFields.find(f => f.ref === sf.ref);
          return newField ? { ...sf, name: newField.name, organisation_id: newField.organisation_id } : sf;
        });
      newWidgetData.finFieldsOptions = newWidgetData.finFieldsOptions.filter(fo =>
        newWidgetData.selectedFinFields.find(f => f.ref === fo.ref)
      );
    }
    // removing any duplicate datasets in case user maps to same dataset more than once
    if (newWidgetData.selectedDatasets && newWidgetData.selectedDatasets!.length > 1) {
      newWidgetData.selectedDatasets = newWidgetData.selectedDatasets.filter(
        (ds, index) => newWidgetData.selectedDatasets!.indexOf(ds) === index
      );
    }
    return newWidgetData;
  }
  return origWidgetData;
};

type DummyLineRefType = 'transactional' | 'cumulative' | 'cash_transactional';

/**
 * adds dummy accounts to available line data for templates
 * no need for i18n because it's used internally to create futrli templates
 * assumptions are not included here as they are handled by the API via include_assumptions
 */
export const addDummyAccounts = (
  fields: AvailableLineDataRefNode[],
  lineRefType: DummyLineRefType,
  organisationId: string
) => {
  const makeDummyAccount = (field: AvailableLineDataRefNode, accountLabel: string) => ({
    ref: `v2:account:${field.ref.substring(field.ref.lastIndexOf(':') + 1)}-${accountLabel.replace(
      ' ',
      ''
    )}:${organisationId}:${lineRefType}`,
    name: `${field.name} ${accountLabel}`,
    classifier: 'account',
    not_selectable: true,
    disabled: false,
    disambiguation_text: null,
    primary_account_name: null,
    assumption_type: null,
    children: []
  });

  return fields.map(field => ({
    ...field,
    children: field.children?.length
      ? field.children
      : [makeDummyAccount(field, 'account 1'), makeDummyAccount(field, 'account 2')]
  }));
};

/**
 * get possible chart types for secondary axis depending on primary chart type
 */
export const getPossibleSecondaryChartTypes = (type: WIDGET_TYPES) => {
  switch (type) {
    case WIDGET_TYPES.BAR_HORIZONTAL:
    case WIDGET_TYPES.BAR_MULTI_HORIZONTAL:
    case WIDGET_TYPES.BAR_VERTICAL:
    case WIDGET_TYPES.BAR_MULTI_VERTICAL:
      return [type, WIDGET_TYPES.LINE, WIDGET_TYPES.LINE_HORIZONTAL, WIDGET_TYPES.AREA];
    case WIDGET_TYPES.LINE:
      return [
        type,
        WIDGET_TYPES.BAR_VERTICAL,
        WIDGET_TYPES.BAR_MULTI_VERTICAL,
        WIDGET_TYPES.LINE_HORIZONTAL,
        WIDGET_TYPES.AREA
      ];
    case WIDGET_TYPES.LINE_HORIZONTAL:
      return [type, WIDGET_TYPES.BAR_VERTICAL, WIDGET_TYPES.BAR_MULTI_VERTICAL, WIDGET_TYPES.LINE, WIDGET_TYPES.AREA];
    case WIDGET_TYPES.AREA:
      return [
        type,
        WIDGET_TYPES.BAR_VERTICAL,
        WIDGET_TYPES.BAR_MULTI_VERTICAL,
        WIDGET_TYPES.LINE,
        WIDGET_TYPES.LINE_HORIZONTAL
      ];
    default:
      return [];
  }
};

/**
 * construct array of objects needed by chart to render data
 */
export const getDatasetsArrayForChart = (
  type: WIDGET_TYPES,
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
  fields: DataField[],
  isStacked: boolean,
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[],
  chartColors: string[],
  data: (FinData | undefined)[],
  colorsOffset: number,
  direction?: 'x' | 'y'
) => {
  const getFieldObject = (
    field: DataField,
    dt: FinData | undefined,
    colorInd: number,
    stackValue: string,
    finDataInd: number,
    isPrimary: boolean
  ) => {
    const fieldColor = getColorForChartData(
      finFieldsOptions.find(e => e.ref === field.ref)?.options?.colors,
      colorInd,
      finDataInd,
      chartColors,
      data.length > 1 ? 0.8 / data.length : 0,
      selectedForecasts
    );
    let values: string[] = [];
    if (dt && dt.fields[field.ref]) {
      values = dt.fields[field.ref] || [];
    }
    const obj: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderColor?: string;
      barPercentage?: number;
      fill?: boolean;
      stepped?: boolean;
      pointRadius?: number;
      borderWidth?: number;
      type?: string;
      order?: number;
      stack: string;
      grouped?: boolean;
      forecastId?: string;
      yAxisID: string | undefined;
      xAxisID: string | undefined;
    } = {
      label: field.name || '',
      data: values.map(e => (isNaN(+e) ? 0 : +e)),
      backgroundColor: fieldColor,
      forecastId: dt?.forecastId,
      stack: stackValue,
      yAxisID: direction === 'x' ? (isPrimary ? 'y' : 'y1') : undefined,
      xAxisID: direction === 'y' ? (isPrimary ? 'x' : 'x1') : undefined
    };

    if (
      [
        WIDGET_TYPES.BAR_HORIZONTAL,
        WIDGET_TYPES.BAR_VERTICAL,
        WIDGET_TYPES.BAR_MULTI_HORIZONTAL,
        WIDGET_TYPES.BAR_MULTI_VERTICAL
      ].includes(type)
    ) {
      obj.type = 'bar';
      obj.barPercentage = data.length < 2 ? 0.8 : 1;
      obj.grouped = true;
      obj.order = 2;
    }
    if ([WIDGET_TYPES.LINE, WIDGET_TYPES.LINE_HORIZONTAL, WIDGET_TYPES.AREA].includes(type)) {
      const isArea = type === WIDGET_TYPES.AREA;
      const isStepped = type === WIDGET_TYPES.LINE_HORIZONTAL;
      obj.type = 'line';
      obj.borderColor = fieldColor;
      obj.backgroundColor = (isArea ? addOpacityToColor(fieldColor, 0.2) : fieldColor) as string;
      obj.fill = isArea;
      obj.stepped = isStepped;
      obj.pointRadius = 1.5;
      obj.borderWidth = 1.5;
      obj.order = 1;
    }
    return obj;
  };

  const datasetsArray: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor?: string;
    barPercentage?: number;
    fill?: boolean;
    stepped?: boolean;
    stack?: string;
    grouped?: boolean;
    forecastId?: string;
    yAxisID: string | undefined;
    xAxisID: string | undefined;
  }[] = [];
  if (isStacked && [WIDGET_TYPES.BAR_HORIZONTAL, WIDGET_TYPES.BAR_VERTICAL].includes(type)) {
    selectedForecasts
      .map(sf => data.find(dt => dt?.forecastId === sf.id))
      .filter(dt => !!dt)
      .forEach((dt: FinData | undefined, i) => {
        fields.forEach((f, j) => {
          if (dt?.fields[f.ref]) {
            datasetsArray.push(
              getFieldObject(
                f,
                dt,
                j + colorsOffset,
                `stack-f-${i}-${colorsOffset ? 'sec' : 'prem'}`,
                i,
                !f.isSecondary
              )
            );
          }
        });
      });
  } else {
    fields?.forEach((f, j) => {
      selectedForecasts
        .map(sf => data.find(dt => dt?.forecastId === sf.id))
        .filter(dt => !!dt)
        .forEach((dt: FinData | undefined, i) => {
          if (dt?.fields[f.ref]) {
            datasetsArray.push(
              getFieldObject(
                f,
                dt,
                j + colorsOffset,
                `stack-ds-${i}-${j}-${colorsOffset ? 'sec' : 'prem'}`,
                i,
                !f.isSecondary
              )
            );
          }
        });
    });
  }
  return datasetsArray;
};

/**
 * construct object of data needed by new line & bar charts
 */
export const getDataForLineBarChart = (
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
  primaryFields: DataField[],
  secondaryFields: DataField[],
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[],
  chartColors: string[],
  data: (FinData | undefined)[],
  type: WIDGET_TYPES,
  secondaryType: WIDGET_TYPES
) => {
  const colors: {
    key: string;
    ref: string;
    color: string;
    forecastId: string;
    name: string;
    isSecondary?: boolean;
    type: WIDGET_TYPES;
  }[] = [];
  let periods: string[] = data[0]?.periods || [];
  if (selectedForecasts.length > 1 && data[0]?.forecastId === 'prior-year-actuals') {
    periods = data[1]?.periods || [];
  }
  const lineBarData: Record<string, string | number>[] = periods.map(p => ({ name: p }));

  primaryFields?.forEach((f, j) => {
    selectedForecasts
      .map(sf => data.find(dt => dt?.forecastId === sf.id))
      .filter(dt => !!dt)
      .forEach((dt: FinData | undefined, i) => {
        if (!dt?.fields[f.ref]) {
          return;
        }
        const values = dt?.fields[f.ref].map(v => (isNaN(+v) ? 0 : +v));
        const fieldColor = getColorForChartData(
          finFieldsOptions.find(e => e.ref === f.ref)?.options?.colors,
          j,
          i,
          chartColors,
          data.length > 1 ? 0.8 / data.length : 0,
          selectedForecasts
        );
        colors.push({
          key: `${dt!.forecastId}#|#${f.ref}`,
          ref: `stack-${dt!.forecastId}`,
          color: fieldColor,
          forecastId: dt!.forecastId || '',
          name: f.name,
          type
        });
        periods.forEach((p, i) => {
          const periodData = lineBarData.find(ld => ld.name === p);
          periodData![`${dt!.forecastId}#|#${f.ref}`] = values[i];
        });
      });
  });

  secondaryFields?.forEach((f, j) => {
    selectedForecasts
      .map(sf => data.find(dt => dt?.forecastId === sf.id))
      .filter(dt => !!dt)
      .forEach((dt: FinData | undefined, i) => {
        if (!dt?.fields[f.ref]) {
          return;
        }
        const values = dt?.fields[f.ref].map(v => (isNaN(+v) ? 0 : +v));
        const fieldColor = getColorForChartData(
          finFieldsOptions.find(e => e.ref === f.ref)?.options?.colors,
          j + primaryFields.length,
          i,
          chartColors,
          data.length > 1 ? 0.8 / data.length : 0,
          selectedForecasts
        );
        colors.push({
          key: `${dt!.forecastId}#|#${f.ref}`,
          ref: `stack-${dt!.forecastId}`,
          color: fieldColor,
          forecastId: dt!.forecastId || '',
          name: f.name,
          isSecondary: true,
          type: secondaryType
        });
        periods.forEach((p, i) => {
          const periodData = lineBarData.find(ld => ld.name === p);
          periodData![`${dt!.forecastId}#|#${f.ref}`] = values[i];
        });
      });
  });

  return { colors: colors.sort((a, b) => WidgetTypesOrder[a.type] - WidgetTypesOrder[b.type]), data: lineBarData };
};

/**
 * construct data needed by new pie & donut charts
 */
export const getDataForPieDonutChart = (
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
  primaryFields: DataField[],
  primaryAxisOptions: AxisOptions | undefined,
  chartColors: string[],
  noDataColors: string[],
  data: (FinData | undefined)[],
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[]
): PieDonutData[] => {
  return selectedForecasts.map((sf, di) => {
    const forecastData = data.find(d => d?.forecastId === sf.id);
    if (!forecastData) {
      return {
        forecastName: sf.name,
        forecastRef: sf.id,
        data: Array.from({ length: 4 }).map((_, i) => ({
          name: '',
          uuid: uuid(),
          value: 10 + Math.random() * 150,
          color: noDataColors[i]
        }))
      };
    }

    return {
      forecastName: sf.name,
      forecastRef: sf.id,
      data: primaryFields.map((field, i) => ({
        name: field.name,
        uuid: `${sf.id}#|#${field.ref}`,
        value: calculateData(forecastData.fields[field.ref], primaryAxisOptions?.valueTreatment || VALUE_TREATMENT.SUM),
        color: getColorForChartData(
          finFieldsOptions.find(e => e.ref === field.ref)?.options?.colors,
          i,
          di,
          chartColors,
          data.length > 1 ? 0.8 / data.length : 0,
          selectedForecasts
        )
      }))
    };
  });
};

/**
 * get report or template owner name
 * @returns string
 */
export const getItemOwnerName = (item: ReportTemplateList | LiveReportList | undefined, members: WorkspaceMember[]) => {
  let ownerName = '';
  if (item?.owner_id) {
    const member = members.find(m => m.id === item.owner_id);
    ownerName = member ? `${member.name.first_name} ${member.name.last_name}` : '';
  }
  return ownerName;
};

/**
 * checks if layout data has dataset
 */
export const hasDataset = (layoutData: LayoutData[], ds: string) =>
  layoutData.some(el => {
    return (
      el.type === CARD_TYPES.WIDGET &&
      el[CARD_TYPES.WIDGET]?.selectedDatasets &&
      (el[CARD_TYPES.WIDGET]?.selectedDatasets || []).includes(ds)
    );
  });

/**
 * checks if layout data has a field
 */
export const hasField = (layoutData: LayoutData[], field: string) =>
  layoutData.some(el => {
    return el.type === CARD_TYPES.WIDGET && (el[CARD_TYPES.WIDGET]?.selectedFinFields || []).find(f => f.ref === field);
  });

/**
 * Returns an array of fields that need mapping, i.e. fields that do not exists in allFinFieldsArray and rowFormulaData
 */
export const mapWorkspaceTemplateDataRefs = (
  finFields: DataField[] | undefined,
  allFinFieldsArray: DataField[],
  rowFormulaData: (RowFormula | ReportingTemplateFormula)[] | undefined
) => {
  if (!finFields) {
    return [];
  }

  const fieldsToMap: string[] = [];
  finFields.forEach(field => {
    const foundField =
      allFinFieldsArray.find(f => f.ref === field.ref) || rowFormulaData?.find(f => f.ref === field.ref);
    if (!foundField) {
      fieldsToMap.push(field.ref);
    }
  });
  return fieldsToMap;
};

export const getDataFieldType = (field: DataField): FIELD_TYPES => {
  if (['profit_and_loss', 'balance_sheet', 'cash-flow'].includes(field.ref)) {
    return FIELD_TYPES.FIN_STATEMENTS;
  }
  if (field.classifier === 'static') {
    return FIELD_TYPES.CATEGORIES;
  }
  if (['group', 'group-by-account-name', 'group-by-org-group-name'].includes(field.classifier)) {
    return FIELD_TYPES.GROUPS;
  }
  if (field.classifier === 'account') {
    return FIELD_TYPES.ACCOUNTS;
  }
  if (field.classifier === 'row-formula') {
    return FIELD_TYPES.FORMULAS;
  }
  if (['freestyle-driver', 'formula-driver', 'driver-group'].includes(field.classifier)) {
    return FIELD_TYPES.DRIVERS;
  }
  return FIELD_TYPES.PREDICTIONS;
};

export const getConsolidationShortCode = (consolidation: Consolidation | undefined, org_id: string) =>
  consolidation ? consolidation.organisations.find(c => c.organisation_id === org_id)?.short_code : '';

export const fieldChildrenHasRef = (field: DataField, ref: string): boolean => {
  if (field.children?.find(e => e.ref === ref)) {
    return true;
  }
  if (field.children?.length) {
    return field.children.some(e => fieldChildrenHasRef(e, ref));
  }
  return false;
};

export const widgetHasTwoAxes = (type: WIDGET_TYPES | undefined) =>
  ![WIDGET_TYPES.TABLE, WIDGET_TYPES.DATA_CARD, WIDGET_TYPES.DONUT, WIDGET_TYPES.PIE].includes(type as WIDGET_TYPES);

/**
 * Gets data references from a report, excluding actual forecast IDs and global financial references.
 * @param report - The report from which to extract data references.
 * @param actualForecastIds - List of actual forecast IDs to exclude.
 * @param globalFinRefs - List of global financial references to exclude.
 * @returns An object containing line data references and forecast reference IDs.
 */
export const getDataReferencesFromReport = (
  report: LiveReportDetail,
  actualForecastIds: string[],
  globalFinRefs: string[]
): { line_data_references: string[]; forecast_reference_ids: string[] } => {
  const dataRefs: string[] = [];
  const datasetRefs: string[] = [];
  const layoutData = (report.data as { pageLayoutData?: LayoutData[] })?.pageLayoutData || [];

  layoutData.forEach(item => {
    if (item.type === CARD_TYPES.WIDGET) {
      item[CARD_TYPES.WIDGET]?.selectedFinFields?.forEach(field => {
        if (!globalFinRefs.includes(field.ref) && !dataRefs.includes(field.ref)) {
          dataRefs.push(field.ref);
        }
      });
      (item[CARD_TYPES.WIDGET]?.selectedDatasets || []).forEach(ds => {
        if (!actualForecastIds.includes(ds) && !datasetRefs.includes(ds)) {
          datasetRefs.push(ds);
        }
      });
    }
  });

  return { line_data_references: dataRefs, forecast_reference_ids: datasetRefs };
};

/**
 * A datasource referenced by a report's widgets, as sent to the backend on report create/update.
 *
 * The generated SDK types flatten the API's `oneOf` into a single interface with every field
 * required, so `forecast_id` is relaxed to optional here: only the `*-forecast` kinds carry one.
 */
export type ReferencedDatasource =
  | (Omit<LiveReportReferencedDatasource, 'forecast_id'> & { forecast_id?: string })
  | (Omit<ConsolidatedLiveReportReferencedDatasource, 'forecast_id'> & { forecast_id?: string });

/**
 * Computes the datasources referenced by a report's widgets, so that create/update requests can
 * declare them explicitly instead of the backend having to derive them.
 *
 * The result is deduplicated: at most one actuals reference and one forecast reference per unique forecast id.
 * Dummy (Futrli template) datasets are ignored, as they are placeholders that are not real datasources.
 *
 * @param pageLayoutData - The report's card/widget data (`report.data.pageLayoutData`).
 * @param organisationId - The organisation id, or the consolidation id when `isConsolidation` is true.
 * @param isConsolidation - Whether the report belongs to a consolidation rather than a single org.
 * @param validForecastIds - Forecast ids returned by the available datasources endpoint (including those the
 * user has no access to). Used forecasts that are not in the set are dropped; when omitted nothing is filtered.
 * @returns The deduplicated list of referenced datasources.
 */
export const getReferencedDatasources = (
  pageLayoutData: LayoutData[] | undefined,
  organisationId: string,
  isConsolidation = false,
  validForecastIds?: ReadonlySet<string>
): ReferencedDatasource[] => {
  let hasActuals = false;
  const forecastIds: string[] = [];
  const shouldValidateForecastIds = !!validForecastIds;

  (pageLayoutData || []).forEach(item => {
    if (item.type !== CARD_TYPES.WIDGET) {
      return;
    }
    (item[CARD_TYPES.WIDGET]?.selectedDatasets || []).forEach(datasetId => {
      if (!datasetId) {
        return;
      }
      if (actualForecastIds.includes(datasetId)) {
        hasActuals = true;
      } else if (dummyForecastIds.includes(datasetId)) {
        // placeholder dataset from a Futrli template that still needs to be mapped
        return;
      } else if ((!shouldValidateForecastIds || validForecastIds?.has(datasetId)) && !forecastIds.includes(datasetId)) {
        forecastIds.push(datasetId);
      }
    });
  });

  // The consolidation id is carried in the `organisationId` route param, hence the single argument.
  const owner = isConsolidation ? { consolidation_id: organisationId } : { organisation_id: organisationId };
  const referencedDatasources: ReferencedDatasource[] = [];

  if (hasActuals) {
    referencedDatasources.push(({
      kind: isConsolidation ? 'consolidation-actuals' : 'organisation-actuals',
      ...owner
    } as unknown) as ReferencedDatasource);
  }

  forecastIds.forEach(forecastId => {
    referencedDatasources.push(({
      kind: isConsolidation ? 'consolidation-forecast' : 'organisation-forecast',
      ...owner,
      forecast_id: forecastId
    } as unknown) as ReferencedDatasource);
  });

  return referencedDatasources;
};

/**
 * Returns the ids of the datasets used by the widgets of a report that are not available to it anymore (e.g. deleted
 * forecasts). Actuals and dummy (template placeholder) datasets are never reported as unavailable.
 *
 * @param pageLayoutData - The report's card/widget data.
 * @param availableDatasetIds - Ids of the forecasts returned by the available datasources endpoint.
 */
export const getUnavailableDatasetIds = (
  pageLayoutData: LayoutData[] | undefined,
  availableDatasetIds: ReadonlySet<string>
): string[] => {
  const unavailableDatasetIds: string[] = [];
  (pageLayoutData || []).forEach(item => {
    (item[CARD_TYPES.WIDGET]?.selectedDatasets || []).forEach(id => {
      if (
        id &&
        !availableDatasetIds.has(id) &&
        !actualForecastIds.includes(id) &&
        !dummyForecastIds.includes(id) &&
        !unavailableDatasetIds.includes(id)
      ) {
        unavailableDatasetIds.push(id);
      }
    });
  });
  return unavailableDatasetIds;
};

/**
 * Display name of a dataset that is no longer available: "Missing dataset", numbered ("Missing dataset 1", "Missing
 * dataset 2"...) when the report has several of them.
 *
 * @param id - The id of the unavailable dataset.
 * @param datasetsToMap - The datasets of the report that still need to be mapped.
 * @param label - The translated "Missing dataset" label.
 */
export const getMissingDatasetName = (id: string, datasetsToMap: MappingData[], label: string) => {
  const missingDatasets = datasetsToMap.filter(d => d.data?.kind === UNAVAILABLE_DATASET_KIND);
  const index = missingDatasets.findIndex(d => d.id === id);
  return missingDatasets.length > 1 && index >= 0 ? `${label} ${index + 1}` : label;
};

export const isChild = (parentField: DataField, ref: string) => {
  const recursivelyCheckChildren = (field: DataField): boolean => {
    if (!field.children?.length) {
      return false;
    }
    if (field.children.some(e => e.ref === ref)) {
      return true;
    } else {
      return field.children.some(e => recursivelyCheckChildren(e));
    }
  };

  return recursivelyCheckChildren(parentField);
};

/**
 * Extracts all line data ref IDs from a formula expression string.
 * Formula expressions reference line data as REF("ref-id") within the expression.
 */
export const extractRefsFromExpression = (expression: string): string[] => {
  const refs: string[] = [];
  const regex = /REF\("([^"]+)"\)/g;
  let match;
  while ((match = regex.exec(expression)) !== null) {
    refs.push(match[1]);
  }
  return refs;
};

/**
 * Resolves formula refs that can be auto-mapped or auto-created, returning:
 * - autoMappedRefs: pairs of [originalRef, mappedRef] for formulas matched by expression
 * - formulasToCreate: formula mapping data for formulas whose line data all exist in the org
 * - remainingRefs: refs that still need manual mapping
 *
 * @param formulaAvailableFinFieldsArray - Available line data specifically for row formulas
 *   (from the row-formulas available-line-data endpoint), which may contain more statics
 *   than the reporting available-line-data endpoint. Falls back to allFinFieldsArray if not provided.
 */
export const resolveFormulaRefs = (
  formulaRefsToMap: string[],
  dataReferenceMapping: ReportMappingData | undefined,
  rowFormulaData: (RowFormula | ReportingTemplateFormula)[] | undefined,
  allFinFieldsArray: DataField[],
  formulaAvailableFinFieldsArray?: DataField[]
): {
  autoMappedRefs: [string, string][];
  formulasToCreate: { ref: string; mappingData: MappingData }[];
  remainingRefs: string[];
} => {
  const autoMappedRefs: [string, string][] = [];
  const formulasToCreate: { ref: string; mappingData: MappingData }[] = [];
  const remainingRefs: string[] = [];

  const allOrgRefs = new Set([
    ...(formulaAvailableFinFieldsArray || allFinFieldsArray).map(f => f.ref),
    ...(rowFormulaData?.map(f => f.ref) || [])
  ]);

  formulaRefsToMap.forEach(ref => {
    const mappingData = dataReferenceMapping?.line_data_references?.[ref];
    if (!mappingData || mappingData.type !== 'row_formula' || !mappingData.data?.expression) {
      remainingRefs.push(ref);
      return;
    }

    const expression = mappingData.data.expression;

    // Step 1: Check if any existing formula in the org has the same expression
    const matchByExpression = rowFormulaData?.find(f => f.expression === expression);
    if (matchByExpression) {
      autoMappedRefs.push([ref, matchByExpression.ref]);
      return;
    }

    // Step 2: Check if all line data refs in the expression exist in the org
    const expressionRefs = extractRefsFromExpression(expression);
    const allRefsExist = expressionRefs.length > 0 && expressionRefs.every(r => allOrgRefs.has(r));
    if (allRefsExist) {
      formulasToCreate.push({ ref, mappingData });
      return;
    }

    // Step 3: Fall back to manual mapping
    remainingRefs.push(ref);
  });

  return { autoMappedRefs, formulasToCreate, remainingRefs };
};
